import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useIsMobile } from '../hooks/responsive';
import { EXTERNAL_LINKS, INTERNAL_LINKS } from '../utils/links';
import { THEME_COLORS } from '../utils/theme';
import { DesktopOnly } from './DesktopOnly';
import { MobileOnly } from './MobileOnly';
import { LogoFont, PrimaryFont } from './styles';

const FooterContainer = styled.div<{ isMobile: boolean }>`
  position: absolute;
  bottom: 0;
  width: 100vw;
  background: ${THEME_COLORS.backgroundLight};

  box-sizing: border-box;

  display: flex;
  flex-flow: row nowrap;
  justify-content: space-between;

  padding: 40px ${({ isMobile }) => (isMobile ? 40 : 100)}px 40px; ;
`;

const Logo = styled(LogoFont)`
  font-size: 40px;
  margin-bottom: 5px;
`;

const Spacer = styled.div`
  width: 75px;
`;

const VerticalStack = styled.div<{ align: 'left' | 'right' }>`
  display: flex;
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: ${({ align }) => (align === 'left' ? 'flex-start' : 'flex-end')};
  padding-left: ${({ align }) => (align === 'left' ? 0 : 10)}px;
  padding-right: ${({ align }) => (align === 'right' ? 0 : 10)}px;
`;

const LinksSection = styled.div<{ isMobile: boolean }>`
  display: flex;
  flex-flow: row nowrap;
  justify-content: flex-end;
  align-items: flex-start;
  text-align: end;
`;

const ExternalFooterLink = styled.a.attrs(() => ({
  target: '_blank',
  rel: 'noopener noreferrer',
}))`
  text-decoration: none;
  color: ${THEME_COLORS.darkText};
  margin-bottom: 8px;
`;

const DarkPrimary = styled(PrimaryFont)`
  color: ${THEME_COLORS.darkText};
  font-weight: 500;
`;

const InternalFooterLink = styled(Link)`
  text-decoration: none;
  color: ${THEME_COLORS.darkText};
  margin-bottom: 8px;
`;

export const Footer: React.FC = () => {
  const { isMobile } = useIsMobile();

  return (
    <FooterContainer isMobile={isMobile}>
      <VerticalStack align="left">
        <Logo>DUBHACKS</Logo>
        <DarkPrimary>building tomorrow together</DarkPrimary>
      </VerticalStack>
      <LinksSection isMobile={isMobile}>
        <DesktopOnly>
          <VerticalStack align="left">
            <ExternalFooterLink href={EXTERNAL_LINKS.CODE_OF_CONDUCT}>Code of Conduct</ExternalFooterLink>
            <InternalFooterLink to={INTERNAL_LINKS.COVID}>COVID-19</InternalFooterLink>
          </VerticalStack>
          <Spacer />
          <VerticalStack align="left">
            <ExternalFooterLink href={EXTERNAL_LINKS.ABOUT_DH}>About DubHacks</ExternalFooterLink>
            <ExternalFooterLink href={EXTERNAL_LINKS.TEAM}>The Team</ExternalFooterLink>
            <InternalFooterLink to={INTERNAL_LINKS.FAQ}>FAQ</InternalFooterLink>
            <InternalFooterLink to={INTERNAL_LINKS.ATTEND}>Attend</InternalFooterLink>
          </VerticalStack>
        </DesktopOnly>
        <MobileOnly>
          <VerticalStack align="right">
            <ExternalFooterLink href={EXTERNAL_LINKS.CODE_OF_CONDUCT}>Code of Conduct</ExternalFooterLink>
            <InternalFooterLink to={INTERNAL_LINKS.COVID}>COVID-19</InternalFooterLink>
            <ExternalFooterLink href={EXTERNAL_LINKS.ABOUT_DH}>About DubHacks</ExternalFooterLink>
            <ExternalFooterLink href={EXTERNAL_LINKS.TEAM}>The Team</ExternalFooterLink>
            <InternalFooterLink to={INTERNAL_LINKS.FAQ}>FAQ</InternalFooterLink>
            <InternalFooterLink to={INTERNAL_LINKS.ATTEND}>Attend</InternalFooterLink>
          </VerticalStack>
        </MobileOnly>
      </LinksSection>
    </FooterContainer>
  );
};
